import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import usePaths from 'src/hooks/use-paths';

type LoginNudgeContextType = {
  isDismissed: boolean;
  dismissNudge: () => void;
};

const LoginNudgeContext = createContext<LoginNudgeContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'login-nudge-dismissed';

type LoginNudgeProviderProps = {
  children: ReactNode;
};

const TIMEOUT_DURATION = 20000;

export const LoginNudgeProvider = ({ children }: LoginNudgeProviderProps): JSX.Element => {
  const { href: baseHref } = usePaths();
  const [isDismissed, setIsDismissed] = useState<boolean>(getPersistedValue(SESSION_STORAGE_KEY, false));
  const router = useRouter();
  const previousPathnameRef = useRef<string | null>(null);

  const dismissNudge = useCallback((): void => {
    setIsDismissed(true);
    setPersistedValue(SESSION_STORAGE_KEY, true, true);
  }, []);

  const currentPathname = router.asPath.split('?')[0].split('#')[0];

  // Dismiss the nudge on page change
  useEffect(() => {
    if (!router.isReady || isDismissed) {
      return;
    }

    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = currentPathname;
      return;
    }

    if (currentPathname !== previousPathnameRef.current) {
      dismissNudge();
      previousPathnameRef.current = currentPathname;
    }
  }, [router.isReady, currentPathname, dismissNudge, isDismissed]);

  // Auto-dismiss after 20 seconds if on a page other than home page
  useEffect(() => {
    if (!router.isReady || isDismissed || !baseHref) {
      return undefined;
    }

    const isOnHomePage = currentPathname === baseHref;
    if (isOnHomePage) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      dismissNudge();
    }, TIMEOUT_DURATION);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [router.isReady, currentPathname, baseHref, isDismissed, dismissNudge]);

  return <LoginNudgeContext.Provider value={{ isDismissed, dismissNudge }}>{children}</LoginNudgeContext.Provider>;
};

export const useLoginNudgeContext = (): LoginNudgeContextType => {
  const context = useContext(LoginNudgeContext);
  if (context === undefined) {
    throw new Error('useLoginNudgeContext must be used within a LoginNudgeProvider');
  }
  return context;
};
